'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, Suspense } from 'react';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import { initPostHog, posthog } from '@/lib/posthog';

/**
 * PostHog Provider for Consumer App
 *
 * Story 6.11: Viral CTA A/B Testing
 *
 * Mirrors creator app pattern (apps/creator/providers/PostHogProvider.tsx).
 * Features:
 * - Initializes PostHog client on mount
 * - Tracks pageviews on route changes (Next.js App Router compatible)
 * - Provides PostHog hooks via context (useFeatureFlagVariantKey, etc.)
 */

function PostHogPageviewTracker(): null {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthogInstance = usePostHog();

  useEffect(() => {
    if (pathname && posthogInstance) {
      let url = window.origin + pathname;
      const params = searchParams?.toString();
      if (params) {
        url = url + `?${params}`;
      }

      posthogInstance.capture('$pageview', {
        $current_url: url,
      });
    }
  }, [pathname, searchParams, posthogInstance]);

  return null;
}

function PageviewTracker(): React.JSX.Element {
  return (
    <Suspense fallback={null}>
      <PostHogPageviewTracker />
    </Suspense>
  );
}

interface PostHogProviderProps {
  children: React.ReactNode;
}

export function PostHogProvider({
  children,
}: PostHogProviderProps): React.JSX.Element {
  useEffect(() => {
    initPostHog();
  }, []);

  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <PageviewTracker />
      {children}
    </PHProvider>
  );
}
